import * as React from 'react';
import axios from 'axios';
import { apiUrl } from 'utils/apiUrl';
import { useAuthContext } from 'contexts/AuthContext';
import { FleetData } from 'App.types';
import { SharedDatabaseMap } from '../learnSiteFilters';

interface SharedDatabaseItem {
  databaseId?: string;
  learnInstanceIds?: string[];
}

interface Page<T> {
  items?: T[];
  nextPageToken?: string;
}

export interface UseSharedDatabasesResult {
  sharedDatabases: SharedDatabaseMap;
  /** True once every shared RDS fleet in the region answered, so a site missing from the map is dedicated. */
  loaded: boolean;
  loading: boolean;
}

// Shared RDS assignments change rarely, so one lookup serves every visit for a few minutes
const CACHE_TTL_MS = 5 * 60 * 1000;
// Guards against a list that never stops returning a page token
const MAX_PAGES = 20;

type LoadedDatabases = Omit<UseSharedDatabasesResult, 'loading'>;

const cache = new Map<string, { promise: Promise<LoadedDatabases>; at: number }>();

/** For tests: forget cached shared database lookups. */
export const clearSharedDatabaseCache = () => cache.clear();

const NOT_LOADED: UseSharedDatabasesResult = { sharedDatabases: {}, loaded: false, loading: false };

const getAllPages = async <T>(url: string, headers: Record<string, string>): Promise<T[]> => {
  const items: T[] = [];
  let nextPageToken: string | undefined;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { data } = await axios.get<Page<T>>(url, {
      headers,
      params: nextPageToken ? { nextPageToken } : undefined,
    });
    items.push(...(data?.items ?? []));
    nextPageToken = data?.nextPageToken;
    if (!nextPageToken) break;
  }
  return items;
};

const loadSharedDatabases = async (
  regions: string[],
  idToken?: string | null,
): Promise<LoadedDatabases> => {
  const headers = { Authorization: `Bearer ${idToken}` };
  const fleets = (await getAllPages<FleetData>(apiUrl('tenancy', 'fleets'), headers)).filter(
    (fleet) => fleet.isSharedRds && regions.includes(fleet.region),
  );

  const responses = await Promise.allSettled(
    fleets.map((fleet) =>
      getAllPages<SharedDatabaseItem>(
        apiUrl('lct', `tenants/${encodeURIComponent(fleet.id)}/databases`),
        headers,
      ),
    ),
  );

  const sharedDatabases: SharedDatabaseMap = {};
  responses.forEach((response) => {
    if (response.status !== 'fulfilled') {
      return;
    }
    response.value.forEach((item) => {
      (item.learnInstanceIds ?? []).forEach((learnTenantId) => {
        if (item.databaseId) {
          sharedDatabases[learnTenantId] = item.databaseId;
        }
      });
    });
  });

  return {
    sharedDatabases,
    loaded: responses.every((response) => response.status === 'fulfilled'),
  };
};

const getSharedDatabases = (regionsKey: string, idToken?: string | null) => {
  const cached = cache.get(regionsKey);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.promise;
  }
  const promise = loadSharedDatabases(regionsKey.split(','), idToken);
  cache.set(regionsKey, { promise, at: Date.now() });
  // A failed lookup is asked again next time rather than cached
  promise.catch(() => cache.delete(regionsKey));
  return promise;
};

/**
 * Learn instance records do not always say which database a site uses, so this reads the shared
 * RDS list of every shared RDS fleet in the regions (the same data as the fleet's Shared RDS tab)
 * and maps each Learn site tenant id to its shared database id. It costs one call per shared RDS
 * fleet, so it only runs while `enabled` (the Database filter is on), and the result is cached.
 */
export const useSharedDatabases = (
  regions: string[],
  enabled: boolean,
): UseSharedDatabasesResult => {
  const { idToken } = useAuthContext();
  const idTokenRef = React.useRef(idToken);
  idTokenRef.current = idToken;
  const regionsKey = regions.join(',');
  const [result, setResult] = React.useState<UseSharedDatabasesResult>(NOT_LOADED);

  React.useEffect(() => {
    if (!enabled) {
      setResult(NOT_LOADED);
      return undefined;
    }
    let cancelled = false;
    setResult({ sharedDatabases: {}, loaded: false, loading: true });

    getSharedDatabases(regionsKey, idTokenRef.current)
      .then((loadedResult) => {
        if (!cancelled) {
          setResult({ ...loadedResult, loading: false });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error('Shared database load error:', error);
          setResult(NOT_LOADED);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [regionsKey, enabled]);

  return result;
};
